// Diagnostic trouble code decoding.
//
// A DTC is two bytes on the wire. The top two bits pick the system letter,
// the next two are the first digit, and the remaining twelve bits are three
// hex digits. So 0x01 0x33 is P0133.

export type DtcKind = 'stored' | 'pending' | 'permanent';
export type DtcSeverity = 'critical' | 'high' | 'moderate' | 'low';

export interface Dtc {
  code: string;
  kind: DtcKind;
  description: string;
  system: string;
  severity: DtcSeverity;
  /** True when the description came from the range fallback rather than the table. */
  generic: boolean;
}

const SYSTEM_LETTER = ['P', 'C', 'B', 'U'] as const;

const SYSTEM_NAME: Record<string, string> = {
  P: 'Powertrain',
  C: 'Chassis',
  B: 'Body',
  U: 'Network',
};

export function bytesToDtc(a: number, b: number): string | null {
  if (a === 0 && b === 0) return null; // padding, not a code
  const letter = SYSTEM_LETTER[(a >> 6) & 0x03];
  const d1 = (a >> 4) & 0x03;
  const d2 = a & 0x0f;
  const d3 = (b >> 4) & 0x0f;
  const d4 = b & 0x0f;
  return `${letter}${d1}${d2.toString(16).toUpperCase()}${d3.toString(16).toUpperCase()}${d4.toString(16).toUpperCase()}`;
}

// The common codes, written the way a technician would say them rather than
// the way SAE J2012 phrases them. Anything absent falls through to a range
// description, which is honest about being approximate.
const DESCRIPTIONS: Record<string, string> = {
  P0010: 'Camshaft position actuator circuit, bank 1',
  P0011: 'Camshaft position, over-advanced, bank 1',
  P0016: 'Crankshaft and camshaft position correlation, bank 1',
  P0017: 'Crankshaft and camshaft position correlation, bank 1 sensor B',
  P0020: 'Camshaft position actuator circuit, bank 2',
  P0031: 'Oxygen sensor heater circuit low, bank 1 sensor 1',
  P0037: 'Oxygen sensor heater circuit low, bank 1 sensor 2',
  P0100: 'Mass air flow circuit fault',
  P0101: 'Mass air flow circuit range or performance',
  P0102: 'Mass air flow circuit low input',
  P0103: 'Mass air flow circuit high input',
  P0106: 'Manifold pressure sensor range or performance',
  P0107: 'Manifold pressure sensor low input',
  P0108: 'Manifold pressure sensor high input',
  P0111: 'Intake air temperature sensor range or performance',
  P0112: 'Intake air temperature sensor low input',
  P0113: 'Intake air temperature sensor high input',
  P0116: 'Coolant temperature sensor range or performance',
  P0117: 'Coolant temperature sensor low input',
  P0118: 'Coolant temperature sensor high input',
  P0121: 'Throttle position sensor range or performance',
  P0122: 'Throttle position sensor low input',
  P0123: 'Throttle position sensor high input',
  P0125: 'Coolant temperature insufficient for closed loop',
  P0128: 'Coolant thermostat below regulating temperature',
  P0130: 'Oxygen sensor circuit, bank 1 sensor 1',
  P0131: 'Oxygen sensor circuit low voltage, bank 1 sensor 1',
  P0132: 'Oxygen sensor circuit high voltage, bank 1 sensor 1',
  P0133: 'Oxygen sensor slow response, bank 1 sensor 1',
  P0134: 'Oxygen sensor no activity, bank 1 sensor 1',
  P0135: 'Oxygen sensor heater circuit, bank 1 sensor 1',
  P0136: 'Oxygen sensor circuit, bank 1 sensor 2',
  P0137: 'Oxygen sensor circuit low voltage, bank 1 sensor 2',
  P0138: 'Oxygen sensor circuit high voltage, bank 1 sensor 2',
  P0139: 'Oxygen sensor slow response, bank 1 sensor 2',
  P0140: 'Oxygen sensor no activity, bank 1 sensor 2',
  P0141: 'Oxygen sensor heater circuit, bank 1 sensor 2',
  P0150: 'Oxygen sensor circuit, bank 2 sensor 1',
  P0153: 'Oxygen sensor slow response, bank 2 sensor 1',
  P0155: 'Oxygen sensor heater circuit, bank 2 sensor 1',
  P0156: 'Oxygen sensor circuit, bank 2 sensor 2',
  P0161: 'Oxygen sensor heater circuit, bank 2 sensor 2',
  P0171: 'System too lean, bank 1',
  P0172: 'System too rich, bank 1',
  P0174: 'System too lean, bank 2',
  P0175: 'System too rich, bank 2',
  P0190: 'Fuel rail pressure sensor circuit',
  P0191: 'Fuel rail pressure sensor range or performance',
  P0201: 'Injector circuit, cylinder 1',
  P0202: 'Injector circuit, cylinder 2',
  P0203: 'Injector circuit, cylinder 3',
  P0204: 'Injector circuit, cylinder 4',
  P0205: 'Injector circuit, cylinder 5',
  P0206: 'Injector circuit, cylinder 6',
  P0217: 'Engine over-temperature condition',
  P0219: 'Engine overspeed condition',
  P0220: 'Throttle position sensor B circuit',
  P0300: 'Random or multiple cylinder misfire detected',
  P0301: 'Cylinder 1 misfire detected',
  P0302: 'Cylinder 2 misfire detected',
  P0303: 'Cylinder 3 misfire detected',
  P0304: 'Cylinder 4 misfire detected',
  P0305: 'Cylinder 5 misfire detected',
  P0306: 'Cylinder 6 misfire detected',
  P0307: 'Cylinder 7 misfire detected',
  P0308: 'Cylinder 8 misfire detected',
  P0316: 'Misfire detected on startup',
  P0325: 'Knock sensor 1 circuit, bank 1',
  P0327: 'Knock sensor 1 circuit low, bank 1',
  P0328: 'Knock sensor 1 circuit high, bank 1',
  P0330: 'Knock sensor 2 circuit, bank 2',
  P0335: 'Crankshaft position sensor A circuit',
  P0336: 'Crankshaft position sensor A range or performance',
  P0340: 'Camshaft position sensor A circuit, bank 1',
  P0341: 'Camshaft position sensor A range or performance, bank 1',
  P0344: 'Camshaft position sensor A intermittent, bank 1',
  P0345: 'Camshaft position sensor A circuit, bank 2',
  P0400: 'Exhaust gas recirculation flow fault',
  P0401: 'Exhaust gas recirculation flow insufficient',
  P0402: 'Exhaust gas recirculation flow excessive',
  P0403: 'Exhaust gas recirculation control circuit',
  P0404: 'Exhaust gas recirculation circuit range or performance',
  P0411: 'Secondary air injection incorrect flow',
  P0420: 'Catalyst efficiency below threshold, bank 1',
  P0421: 'Warm-up catalyst efficiency below threshold, bank 1',
  P0430: 'Catalyst efficiency below threshold, bank 2',
  P0440: 'Evaporative emission system fault',
  P0441: 'Evaporative emission purge flow incorrect',
  P0442: 'Evaporative emission small leak detected',
  P0443: 'Evaporative emission purge control valve circuit',
  P0446: 'Evaporative emission vent control circuit',
  P0449: 'Evaporative emission vent valve circuit',
  P0451: 'Evaporative emission pressure sensor range or performance',
  P0455: 'Evaporative emission large leak detected',
  P0456: 'Evaporative emission very small leak detected',
  P0457: 'Evaporative emission leak, fuel cap loose or missing',
  P0460: 'Fuel level sensor circuit',
  P0463: 'Fuel level sensor circuit high input',
  P0480: 'Cooling fan 1 control circuit',
  P0500: 'Vehicle speed sensor A fault',
  P0505: 'Idle air control system fault',
  P0506: 'Idle speed lower than expected',
  P0507: 'Idle speed higher than expected',
  P0520: 'Engine oil pressure sensor circuit',
  P0524: 'Engine oil pressure too low',
  P0530: 'Air conditioning refrigerant pressure sensor circuit',
  P0532: 'Air conditioning refrigerant pressure sensor low',
  P0562: 'System voltage low',
  P0563: 'System voltage high',
  P0571: 'Brake switch A circuit',
  P0601: 'Control module memory check sum error',
  P0603: 'Control module keep-alive memory error',
  P0605: 'Control module read-only memory error',
  P0606: 'Control module processor fault',
  P0700: 'Transmission control system, malfunction indicator requested',
  P0701: 'Transmission control system range or performance',
  P0705: 'Transmission range sensor circuit',
  P0711: 'Transmission fluid temperature sensor range or performance',
  P0715: 'Input or turbine speed sensor circuit',
  P0720: 'Output speed sensor circuit',
  P0730: 'Incorrect gear ratio',
  P0740: 'Torque converter clutch circuit',
  P0741: 'Torque converter clutch stuck off',
  P0750: 'Shift solenoid A fault',
  P0755: 'Shift solenoid B fault',
  C0035: 'Left front wheel speed sensor circuit',
  C0040: 'Right front wheel speed sensor circuit',
  C0045: 'Left rear wheel speed sensor circuit',
  C0050: 'Right rear wheel speed sensor circuit',
  C0110: 'Pump motor circuit fault',
  C0121: 'Valve relay circuit fault',
  C0265: 'Electronic brake control relay circuit',
  B0001: 'Driver airbag deployment control',
  B0004: 'Passenger airbag deployment control',
  B1000: 'Electronic control unit fault',
  U0001: 'High speed CAN communication bus',
  U0100: 'Lost communication with engine control module',
  U0101: 'Lost communication with transmission control module',
  U0121: 'Lost communication with ABS control module',
  U0140: 'Lost communication with body control module',
  U0155: 'Lost communication with instrument panel cluster',
  U0401: 'Invalid data received from engine control module',
};

// Coarse ranges for anything the table above does not carry. The point is to
// tell the user what area of the car the code belongs to, not to invent detail.
interface RangeRule {
  test: RegExp;
  label: string;
  severity: DtcSeverity;
}

const RANGES: RangeRule[] = [
  { test: /^P00[0-9A-F]{2}$/, label: 'Fuel and air metering, or auxiliary emission control', severity: 'moderate' },
  { test: /^P01[0-9A-F]{2}$/, label: 'Fuel and air metering', severity: 'moderate' },
  { test: /^P02[0-9A-F]{2}$/, label: 'Fuel and air metering, injector circuit', severity: 'high' },
  { test: /^P03[0-9A-F]{2}$/, label: 'Ignition system or misfire', severity: 'high' },
  { test: /^P04[0-9A-F]{2}$/, label: 'Auxiliary emission controls', severity: 'moderate' },
  { test: /^P05[0-9A-F]{2}$/, label: 'Vehicle speed, idle control, auxiliary inputs', severity: 'moderate' },
  { test: /^P06[0-9A-F]{2}$/, label: 'Computer output circuit', severity: 'high' },
  { test: /^P0[78][0-9A-F]{2}$/, label: 'Transmission', severity: 'high' },
  { test: /^P1[0-9A-F]{3}$/, label: 'Manufacturer-specific powertrain code', severity: 'moderate' },
  { test: /^C[0-9A-F]{4}$/, label: 'Chassis: brakes, steering or suspension', severity: 'high' },
  { test: /^B[0-9A-F]{4}$/, label: 'Body: restraints, climate or comfort systems', severity: 'moderate' },
  { test: /^U[0-9A-F]{4}$/, label: 'Network communication between modules', severity: 'high' },
];

const CRITICAL = /^P0(21[79]|30[0-8]|33[5-9]|34[0-9])$/;
const LOW = /^P0(44[0-9]|45[0-9]|46[0-9])$/;

export function severityFor(code: string): DtcSeverity {
  if (CRITICAL.test(code)) return 'critical';
  if (LOW.test(code)) return 'low';
  const range = RANGES.find((r) => r.test.test(code));
  return range?.severity ?? 'moderate';
}

export function describeDtc(code: string, kind: DtcKind): Dtc {
  const upper = code.toUpperCase();
  const known = DESCRIPTIONS[upper];
  const range = RANGES.find((r) => r.test.test(upper));
  return {
    code: upper,
    kind,
    description: known ?? range?.label ?? 'Unrecognised code',
    system: SYSTEM_NAME[upper[0]] ?? 'Unknown',
    severity: severityFor(upper),
    generic: !known,
  };
}

/**
 * Parse an ELM327 response to mode 03, 07 or 0A.
 *
 * Handles both single-frame replies and the numbered segment format the
 * adapter emits once the payload exceeds seven bytes, and copes with the
 * count byte being present or absent, which varies by adapter and protocol.
 */
export function parseDtcResponse(raw: string, kind: DtcKind, expectedMode: number): Dtc[] {
  const responseByte = (expectedMode + 0x40).toString(16).toUpperCase().padStart(2, '0');
  const codes: Dtc[] = [];
  const seen = new Set<string>();

  // Every responding module is parsed on its own, then the results are unioned.
  for (const response of splitResponses(raw)) {
    const start = response.indexOf(responseByte);
    if (start < 0) continue;

    let bytes = hexToBytes(response.slice(start + 2));
    if (bytes.length === 0) continue;

    // A leading count byte appears on CAN but not on every K-line adapter.
    // Two bytes per code means an odd remainder can only be a count, and an even
    // remainder is a count only when the value matches the codes that follow.
    const odd = bytes.length % 2 === 1;
    if (odd || bytes[0] * 2 + 1 === bytes.length) bytes = bytes.slice(1);

    for (let i = 0; i + 1 < bytes.length; i += 2) {
      const code = bytesToDtc(bytes[i], bytes[i + 1]);
      if (!code || seen.has(code)) continue;
      seen.add(code);
      codes.push(describeDtc(code, kind));
    }
  }
  return codes;
}

/**
 * Mode 01 PID 01: malfunction lamp state and the stored code count.
 *
 * Each module reports its own lamp request and its own count, so the lamp is
 * on if any module asks for it and the count is the total across the bus.
 */
export function parseMilStatus(raw: string): { milOn: boolean; dtcCount: number } | null {
  let milOn = false;
  let dtcCount = 0;
  let answered = false;

  for (const response of splitResponses(raw)) {
    const start = response.indexOf('4101');
    if (start < 0) continue;
    const bytes = hexToBytes(response.slice(start + 4));
    if (bytes.length === 0) continue;
    answered = true;
    milOn = milOn || (bytes[0] & 0x80) !== 0;
    dtcCount += bytes[0] & 0x7f;
  }

  return answered ? { milOn, dtcCount } : null;
}

// ---------- shared response cleanup ----------

/**
 * Split raw adapter output into one entry per responding module.
 *
 * This distinction is the whole ballgame on a multi-ECU vehicle. Ask a truck
 * for stored codes and every module answers, so the adapter prints:
 *
 *     4300
 *     4300
 *
 * Two modules, each reporting zero codes. Concatenating those into "43004300"
 * and parsing once produces a phantom DTC, because the second module's
 * response header 0x43 0x00 decodes as C0300. Real vehicles with nothing wrong
 * were reporting three chassis faults. Keep the replies separate.
 *
 * Numbered segment lines are the exception: those are one logical response
 * split across ISO-TP frames, so they reassemble into a single entry.
 */
export function splitResponses(raw: string): string[] {
  const lines = raw
    .replace(/>/g, '')
    .split(/[\r\n]+/)
    .map((l) => l.trim())
    .filter(Boolean)
    .filter((l) => !/^(SEARCHING|BUS INIT|OK|NO DATA|STOPPED|UNABLE TO CONNECT|CAN ERROR|\?)/i.test(l));

  const responses: string[] = [];
  let segments: string[] = [];
  let lastIndex = -1;

  const flushSegments = () => {
    if (segments.length === 0) return;
    responses.push(segments.join(''));
    segments = [];
    lastIndex = -1;
  };

  for (const line of lines) {
    const seg = /^([0-9A-F]):\s*(.*)$/i.exec(line);
    if (seg) {
      const index = parseInt(seg[1], 16);
      // Segment counters climb within one message. A counter that does not
      // advance means a second module started its own reply, so the previous
      // message is complete. Without this, two modules answering mode 06 or
      // mode 09 interleave into one string and every field lands on the wrong
      // byte - which reads as dozens of failed tests rather than as an error.
      if (index <= lastIndex) flushSegments();
      segments.push(seg[2].replace(/\s+/g, '').toUpperCase());
      lastIndex = index;
      continue;
    }

    const cleaned = line.replace(/\s+/g, '').toUpperCase();
    if (!/^[0-9A-F]+$/.test(cleaned)) continue;

    // Data always arrives as whole bytes, so an odd-length line is the ISO-TP
    // total-length header. It precedes each segmented message, which makes it
    // a second, independent boundary marker.
    if (cleaned.length % 2 === 1) {
      flushSegments();
      continue;
    }

    flushSegments();
    responses.push(cleaned);
  }

  flushSegments();
  return responses;
}

/**
 * The first module's reply, for reads where one answer is all we need.
 *
 * Live data PIDs are the case: every module may report engine speed, and they
 * agree closely enough that the first answer is the answer.
 */
export function joinSegments(raw: string): string {
  return splitResponses(raw)[0] ?? '';
}

export function hexToBytes(hex: string): number[] {
  const clean = hex.replace(/[^0-9A-F]/gi, '');
  const out: number[] = [];
  for (let i = 0; i + 1 < clean.length; i += 2) {
    out.push(parseInt(clean.slice(i, i + 2), 16));
  }
  return out;
}
